// src/utils/TimeSlotValidator.js
// Comprehensive time slot validation utilities for booking conflict prevention

export class TimeSlotValidator {
  
  // Standard business hours (can be customized per business in future)
  static DEFAULT_BUSINESS_HOURS = {
    monday: { start: '09:00', end: '17:00', closed: false },
    tuesday: { start: '09:00', end: '17:00', closed: false },
    wednesday: { start: '09:00', end: '17:00', closed: false },
    thursday: { start: '09:00', end: '17:00', closed: false },
    friday: { start: '09:00', end: '17:00', closed: false },
    saturday: { start: '10:00', end: '16:00', closed: false },
    sunday: { start: '10:00', end: '16:00', closed: true }
  };

  // Minimum and maximum booking durations (in minutes)
  static MIN_BOOKING_DURATION = 15;
  static MAX_BOOKING_DURATION = 480; // 8 hours
  
  // Minimum advance booking time (in minutes)
  static MIN_ADVANCE_BOOKING = 60; // 1 hour
  
  // Maximum advance booking time (in days)
  static MAX_ADVANCE_BOOKING = 365; // 1 year

  /**
   * Validate a single time slot
   */
  static validateTimeSlot(dateTime, duration = 60, businessHours = null) {
    const errors = [];
    const warnings = [];
    const info = [];
    
    try {
      const bookingDate = new Date(dateTime);
      const now = new Date();
      
      // Basic date validation
      if (isNaN(bookingDate.getTime())) {
        errors.push('Invalid date/time format');
        return { isValid: false, errors, warnings, info };
      }
      
      // Past date validation
      if (bookingDate <= now) {
        errors.push('Booking time must be in the future');
      }
      
      // Too far in advance validation
      const maxAdvanceDate = new Date(now.getTime() + (this.MAX_ADVANCE_BOOKING * 24 * 60 * 60 * 1000));
      if (bookingDate > maxAdvanceDate) {
        warnings.push(`Booking is more than ${this.MAX_ADVANCE_BOOKING} days in advance`);
      }
      
      // Minimum advance time validation
      const minAdvanceDate = new Date(now.getTime() + (this.MIN_ADVANCE_BOOKING * 60 * 1000));
      if (bookingDate < minAdvanceDate) {
        warnings.push(`Booking should be at least ${this.MIN_ADVANCE_BOOKING} minutes in advance`);
      }
      
      // Duration validation
      if (duration < this.MIN_BOOKING_DURATION) {
        warnings.push(`Booking duration is very short (${duration} minutes, minimum recommended: ${this.MIN_BOOKING_DURATION})`);
      }
      
      if (duration > this.MAX_BOOKING_DURATION) {
        warnings.push(`Booking duration is very long (${duration} minutes, maximum recommended: ${this.MAX_BOOKING_DURATION})`);
      }
      
      // Business hours validation
      const businessHoursCheck = this.validateBusinessHours(bookingDate, duration, businessHours);
      if (!businessHoursCheck.isValid) {
        errors.push(...businessHoursCheck.errors);
        warnings.push(...businessHoursCheck.warnings);
      }
      
      // Time slot alignment validation (15-minute increments)
      const minutes = bookingDate.getMinutes();
      if (minutes % 15 !== 0) {
        warnings.push('Booking time should align to 15-minute increments (e.g., :00, :15, :30, :45)');
      }
      
      // Weekend booking info
      const dayOfWeek = bookingDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        info.push('Weekend booking detected');
      }
      
      // Holiday validation (placeholder for future integration)
      const holidayCheck = this.checkForHolidays(bookingDate);
      if (holidayCheck.isHoliday) {
        warnings.push(`Booking is on a holiday: ${holidayCheck.holidayName}`);
      }
      
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        info,
        validatedAt: new Date().toISOString(),
        bookingDate: bookingDate.toISOString(),
        duration,
        dayOfWeek: this.getDayName(dayOfWeek)
      };
      
    } catch (error) {
      return {
        isValid: false,
        errors: ['Time slot validation failed due to system error'],
        warnings: [],
        info: [],
        validatedAt: new Date().toISOString(),
        systemError: error.message
      };
    }
  }

  /**
   * Validate business hours
   */
  static validateBusinessHours(bookingDate, duration, customBusinessHours = null) {
    const errors = [];
    const warnings = [];
    
    try {
      const businessHours = customBusinessHours || this.DEFAULT_BUSINESS_HOURS;
      const dayOfWeek = this.getDayName(bookingDate.getDay());
      const dayHours = businessHours[dayOfWeek];
      
      if (!dayHours) {
        warnings.push(`Business hours not defined for ${dayOfWeek}`);
        return { isValid: true, errors, warnings }; // Allow booking if hours not defined
      }
      
      if (dayHours.closed) {
        errors.push(`Business is closed on ${dayOfWeek}`);
        return { isValid: false, errors, warnings };
      }
      
      // Parse business hours
      const [startHour, startMinute] = dayHours.start.split(':').map(Number);
      const [endHour, endMinute] = dayHours.end.split(':').map(Number);
      
      // Create business hours for the booking date
      const businessStart = new Date(bookingDate);
      businessStart.setHours(startHour, startMinute, 0, 0);
      
      const businessEnd = new Date(bookingDate);
      businessEnd.setHours(endHour, endMinute, 0, 0);
      
      // Calculate booking end time
      const bookingEnd = new Date(bookingDate.getTime() + (duration * 60 * 1000));
      
      // Validate booking start time
      if (bookingDate < businessStart) {
        errors.push(`Booking starts before business hours (${dayHours.start})`);
      }
      
      // Validate booking end time
      if (bookingEnd > businessEnd) {
        errors.push(`Booking ends after business hours (${dayHours.end})`);
      }
      
      // Check if booking spans entire business day
      if (duration >= ((endHour - startHour) * 60 + (endMinute - startMinute))) {
        warnings.push('Booking spans most or all of the business day');
      }
      
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        businessHours: dayHours,
        dayOfWeek
      };
      
    } catch (error) {
      return {
        isValid: true, // Default to valid to avoid breaking bookings
        errors: [],
        warnings: ['Business hours validation failed'],
        systemError: error.message
      };
    }
  }

  /**
   * Check if date falls on a holiday (placeholder for future integration)
   */
  static checkForHolidays(bookingDate) {
    try {
      // This would integrate with your holiday calendar system
      // For now, check for major holidays
      
      const month = bookingDate.getMonth() + 1; // getMonth() returns 0-11
      const day = bookingDate.getDate();
      
      const majorHolidays = {
        '1-1': 'New Year\'s Day',
        '7-1': 'Canada Day',
        '7-4': 'Independence Day (US)',
        '12-25': 'Christmas Day',
        '12-26': 'Boxing Day'
      };
      
      const dateKey = `${month}-${day}`;
      const holidayName = majorHolidays[dateKey];
      
      return {
        isHoliday: !!holidayName,
        holidayName: holidayName || null,
        checkedDate: bookingDate.toISOString().split('T')[0]
      };
      
    } catch (error) {
      return {
        isHoliday: false,
        holidayName: null,
        error: 'Holiday check failed'
      };
    }
  }

  /**
   * Validate time slot overlaps
   */
  static checkTimeSlotOverlap(slot1, slot2) {
    try {
      const start1 = new Date(slot1.start);
      const end1 = new Date(slot1.end || new Date(start1.getTime() + (slot1.duration || 60) * 60000));
      
      const start2 = new Date(slot2.start);
      const end2 = new Date(slot2.end || new Date(start2.getTime() + (slot2.duration || 60) * 60000));
      
      const hasOverlap = start1 < end2 && start2 < end1;
      
      let overlapDetails = null;
      if (hasOverlap) {
        const overlapStart = new Date(Math.max(start1.getTime(), start2.getTime()));
        const overlapEnd = new Date(Math.min(end1.getTime(), end2.getTime()));
        const overlapDuration = Math.round((overlapEnd.getTime() - overlapStart.getTime()) / 60000);
        
        overlapDetails = {
          start: overlapStart.toISOString(),
          end: overlapEnd.toISOString(),
          duration: overlapDuration
        };
      }
      
      return {
        hasOverlap,
        overlapDetails,
        slot1: {
          start: start1.toISOString(),
          end: end1.toISOString(),
          duration: Math.round((end1.getTime() - start1.getTime()) / 60000)
        },
        slot2: {
          start: start2.toISOString(),
          end: end2.toISOString(),
          duration: Math.round((end2.getTime() - start2.getTime()) / 60000)
        }
      };
      
    } catch (error) {
      return {
        hasOverlap: false,
        error: 'Overlap check failed',
        details: error.message
      };
    }
  }

  /**
   * Generate available time slots for a given date
   */
  static generateAvailableTimeSlots(date, duration = 60, businessHours = null, interval = 30) {
    try {
      const slots = [];
      const targetDate = new Date(date);
      const dayHours = businessHours || this.DEFAULT_BUSINESS_HOURS[this.getDayName(targetDate.getDay())];
      
      if (!dayHours || dayHours.closed) {
        return {
          success: true,
          slots: [],
          reason: 'Business closed on this day'
        };
      }
      
      // Parse business hours
      const [startHour, startMinute] = dayHours.start.split(':').map(Number);
      const [endHour, endMinute] = dayHours.end.split(':').map(Number);
      
      // Generate time slots
      let currentTime = new Date(targetDate);
      currentTime.setHours(startHour, startMinute, 0, 0);
      
      const businessEnd = new Date(targetDate);
      businessEnd.setHours(endHour, endMinute, 0, 0);
      
      while (currentTime < businessEnd) {
        const slotEnd = new Date(currentTime.getTime() + (duration * 60000));
        
        // Check if slot fits within business hours
        if (slotEnd <= businessEnd) {
          const validation = this.validateTimeSlot(currentTime.toISOString(), duration, businessHours);
          
          slots.push({
            start: currentTime.toISOString(),
            end: slotEnd.toISOString(),
            time: currentTime.toTimeString().substring(0, 5),
            duration,
            isValid: validation.isValid,
            hasWarnings: validation.warnings.length > 0,
            validation
          });
        }
        
        // Move to next interval
        currentTime = new Date(currentTime.getTime() + (interval * 60000));
      }
      
      return {
        success: true,
        slots,
        date: targetDate.toISOString().split('T')[0],
        duration,
        interval,
        businessHours: dayHours,
        totalSlots: slots.length,
        validSlots: slots.filter(slot => slot.isValid).length
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message,
        slots: []
      };
    }
  }

  /**
   * Validate multiple time slots for batch operations
   */
  static validateMultipleTimeSlots(timeSlots) {
    try {
      const results = [];
      const conflicts = [];
      
      // Validate each slot individually
      timeSlots.forEach((slot, index) => {
        const validation = this.validateTimeSlot(slot.dateTime, slot.duration);
        
        results.push({
          index,
          slot,
          validation,
          isValid: validation.isValid
        });
      });
      
      // Check for conflicts between slots
      for (let i = 0; i < timeSlots.length; i++) {
        for (let j = i + 1; j < timeSlots.length; j++) {
          const slot1 = timeSlots[i];
          const slot2 = timeSlots[j];
          
          const overlapCheck = this.checkTimeSlotOverlap(
            { start: slot1.dateTime, duration: slot1.duration },
            { start: slot2.dateTime, duration: slot2.duration }
          );
          
          if (overlapCheck.hasOverlap) {
            conflicts.push({
              slot1Index: i,
              slot2Index: j,
              slot1: slot1,
              slot2: slot2,
              overlapDetails: overlapCheck.overlapDetails
            });
          }
        }
      }
      
      const validSlots = results.filter(r => r.isValid).length;
      const hasConflicts = conflicts.length > 0;
      
      return {
        success: true,
        results,
        conflicts,
        summary: {
          totalSlots: timeSlots.length,
          validSlots,
          invalidSlots: timeSlots.length - validSlots,
          internalConflicts: conflicts.length,
          hasConflicts
        },
        validatedAt: new Date().toISOString()
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message,
        results: [],
        conflicts: [],
        summary: {
          totalSlots: timeSlots.length,
          validSlots: 0,
          invalidSlots: timeSlots.length,
          internalConflicts: 0,
          hasConflicts: false
        }
      };
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Get day name from day number
   */
  static getDayName(dayNumber) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayNumber] || 'unknown';
  }

  /**
   * Parse time string to minutes
   */
  static timeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Convert minutes to time string
   */
  static minutesToTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  /**
   * Get next available time slot
   */
  static getNextAvailableSlot(currentDateTime, duration = 60, businessHours = null) {
    try {
      const current = new Date(currentDateTime);
      const today = new Date(current);
      
      // Try today first
      let availableSlots = this.generateAvailableTimeSlots(today, duration, businessHours);
      
      if (availableSlots.success && availableSlots.slots.length > 0) {
        // Find next slot after current time
        const nextSlot = availableSlots.slots.find(slot => new Date(slot.start) > current);
        if (nextSlot) {
          return {
            success: true,
            nextSlot,
            date: today.toISOString().split('T')[0]
          };
        }
      }
      
      // Try next business day
      let nextDay = new Date(today);
      let attempts = 0;
      const maxAttempts = 14; // Check up to 2 weeks ahead
      
      while (attempts < maxAttempts) {
        nextDay.setDate(nextDay.getDate() + 1);
        attempts++;
        
        availableSlots = this.generateAvailableTimeSlots(nextDay, duration, businessHours);
        
        if (availableSlots.success && availableSlots.slots.length > 0) {
          const firstValidSlot = availableSlots.slots.find(slot => slot.isValid);
          if (firstValidSlot) {
            return {
              success: true,
              nextSlot: firstValidSlot,
              date: nextDay.toISOString().split('T')[0],
              daysAhead: attempts
            };
          }
        }
      }
      
      return {
        success: false,
        error: 'No available slots found in the next 2 weeks'
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}